import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAPIClient } from '@/hooks/useAPIClient';
import { DocumentUploadResponse } from '@/types/api';

export interface UploadOptions {
  module: string;
  subpath?: string;
  /** Optional mapping of filename to machine code for per-file machine assignment */
  file_machines?: Record<string, string>;
}

export const useDocumentUpload = () => {
  const apiClient = useAPIClient();
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: async ({
      files,
      options
    }: {
      files: File[];
      options: UploadOptions;
    }): Promise<DocumentUploadResponse> => {
      return await apiClient.upload.uploadDocuments(files, {
        module: options.module,
        subpath: options.subpath,
        file_machines: options.file_machines,
      });
    },
    onSuccess: () => {
      // Invalidate relevant queries after successful upload
      queryClient.invalidateQueries({ queryKey: ['upload-folder-tree'] });
      queryClient.invalidateQueries({ queryKey: ['input-files'] });
      queryClient.invalidateQueries({ queryKey: ['document-status'] });
      queryClient.invalidateQueries({ queryKey: ['system-status'] });
    },
  });

  return {
    uploadDocuments: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
    uploadError: uploadMutation.error,
    uploadData: uploadMutation.data,
    reset: uploadMutation.reset,
  };
};